import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ExternalLink, Power, Upload, Download, Waves, Tag, X } from "lucide-react";

function BrandPicker({ selected, onChange, testIdPrefix = "sub-brand" }) {
  const sel = selected || [];
  const q = useQuery({
    queryKey: ["products-brief"],
    queryFn: () => api.get("/products").then(r => r.data),
  });
  const items = q.data || [];
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-picker`}>
      {items.length === 0 && <span className="text-[11px] text-zinc-500 font-mono">no brands yet</span>}
      {items.map(p => {
        const on = sel.includes(p.id);
        return (
          <button type="button" key={p.id} onClick={() => toggle(p.id)}
            data-testid={`${testIdPrefix}-chip-${p.id}`}
            className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 border ${on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            {p.name}
          </button>
        );
      })}
    </div>
  );
}

export default function Subreddits() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", description: "", rules: "", is_drift_pool: false, product_ids: [], category_id: "" });
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("all");   // 'all' | 'drift'
  const [catFilter, setCatFilter] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const fileRef = useRef(null);

  const catsQ = useQuery({ queryKey: ["subreddit-categories"], queryFn: () => api.get("/subreddit-categories").then(r => r.data) });
  const categories = catsQ.data || [];

  const createCat = useMutation({
    mutationFn: (body) => api.post("/subreddit-categories", body),
    onSuccess: () => { setNewCatName(""); toast.success("Category added"); qc.invalidateQueries({queryKey:["subreddit-categories"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const delCat = useMutation({
    mutationFn: (id) => api.delete(`/subreddit-categories/${id}`),
    onSuccess: () => { toast.success("Category removed"); qc.invalidateQueries({queryKey:["subreddit-categories"]}); qc.invalidateQueries({queryKey:["subreddits"]}); },
  });

  const q = useQuery({
    queryKey: ["subreddits"],
    queryFn: () => api.get("/subreddits").then(r => r.data),
  });

  const create = useMutation({
    mutationFn: (body) => api.post("/subreddits", body),
    onSuccess: () => { setForm({ name:"", description:"", rules:"", is_drift_pool:false, product_ids: [], category_id: "" }); toast.success("Subreddit saved"); qc.invalidateQueries({queryKey:["subreddits"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/subreddits/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({queryKey:["subreddits"]}); },
  });

  const toggle = useMutation({
    mutationFn: ({id, active}) => api.patch(`/subreddits/${id}`, { name: "placeholder", active }),
    onSuccess: () => qc.invalidateQueries({queryKey:["subreddits"]}),
  });

  const toggleDrift = useMutation({
    mutationFn: ({id, is_drift_pool}) => api.patch(`/subreddits/${id}`, { name: "placeholder", is_drift_pool }),
    onSuccess: () => { toast.success("Drift pool updated"); qc.invalidateQueries({queryKey:["subreddits"]}); },
  });

  const saveEdit = useMutation({
    mutationFn: (body) => api.patch(`/subreddits/${editing.id}`, body),
    onSuccess: () => { setEditing(null); toast.success("Updated"); qc.invalidateQueries({queryKey:["subreddits"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const bulkImport = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("is_drift_pool", tab === "drift" ? "true" : "false");
      return api.post("/subreddits/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: (r) => {
      const {added, skipped, total_errors} = r.data;
      toast.success(`Imported ${added} · skipped ${skipped}${total_errors ? ` · ${total_errors} errors` : ""}`);
      qc.invalidateQueries({queryKey:["subreddits"]});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Import failed"),
  });

  const onFilePicked = async (file) => {
    if (!file) return;
    bulkImport.mutate(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadSample = async () => {
    const r = await api.get("/subreddits/sample-xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    }));
    const a = document.createElement("a");
    a.href = url; a.download = "subreddits_template.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const submit = () => {
    if (!form.name.trim()) { toast.error("Enter subreddit name (e.g. r/gardening)"); return; }
    create.mutate({
      name: form.name.trim(),
      description: form.description.trim(),
      rules: form.rules.trim(),
      is_drift_pool: form.is_drift_pool || tab === "drift",
      product_ids: form.product_ids,
      category_id: form.category_id || null,
    });
  };

  const filtered = (q.data || []).filter(s =>
    (tab === "all" || s.is_drift_pool) &&
    (!catFilter || s.category_id === catFilter)
  );

  return (
    <Layout title="Target Subreddits." subtitle="Where your campaigns can post & engage · drift pool for organic tasks">
      {/* Tabs + Bulk import */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-1 border border-white/10 bg-[#050505] w-fit">
          <button
            onClick={() => setTab("all")}
            data-testid="tab-all"
            className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest ${tab === "all" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"}`}
          >All ({(q.data || []).length})</button>
          <button
            onClick={() => setTab("drift")}
            data-testid="tab-drift"
            className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5 ${tab === "drift" ? "bg-[#007AFF] text-white" : "text-zinc-400 hover:text-white"}`}
          ><Waves className="w-3 h-3"/> Drift Pool ({(q.data || []).filter(s => s.is_drift_pool).length})</button>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                 onChange={(e) => onFilePicked(e.target.files?.[0])}
                 data-testid="bulk-import-input" className="hidden"/>
          <button
            onClick={downloadSample}
            data-testid="download-sample-btn"
            className="text-[11px] font-mono uppercase tracking-widest border border-white/10 hover:border-white/30 text-zinc-300 px-3 py-1.5 flex items-center gap-1.5"
          >
            <Download className="w-3 h-3"/> Sample XLSX
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={bulkImport.isPending}
            data-testid="bulk-import-btn"
            className="text-[11px] font-mono uppercase tracking-widest bg-[#FF3B30] hover:bg-[#FF5A50] text-white px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
          >
            {bulkImport.isPending ? <Loader2 className="w-3 h-3 animate-spin"/> : <Upload className="w-3 h-3"/>}
            Bulk XLSX{tab === "drift" ? " → drift" : ""}
          </button>
        </div>
      </div>

      {tab === "drift" && (
        <div className="mb-4 border border-[#007AFF]/40 bg-[#007AFF]/5 p-3 text-[12px] text-[#007AFF] leading-relaxed">
          🌊 <b>Drift Pool</b> — organic engagement subreddits. Drift tasks (daily benign activity) are generated ONLY from this pool. Agents never comment/upvote on subreddits outside it.
        </div>
      )}

      {/* Categories bar */}
      <div className="mb-4 border border-white/10 p-3 flex flex-wrap items-center gap-2" data-testid="category-bar">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1 flex items-center gap-1"><Tag className="w-3 h-3"/> categories:</span>
        <button
          data-testid="category-chip-all"
          onClick={() => setCatFilter("")}
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${!catFilter ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
        >all</button>
        {categories.map(c => (
          <div key={c.id} className="flex items-center gap-0.5">
            <button
              data-testid={`category-chip-${c.id}`}
              onClick={() => setCatFilter(c.id)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${catFilter === c.id ? "text-white" : "text-zinc-400 hover:border-white/30"}`}
              style={{ borderColor: catFilter === c.id ? c.color : undefined, background: catFilter === c.id ? `${c.color}22` : undefined }}
            >{c.name}</button>
            <button
              onClick={() => { if (window.confirm(`Delete category ${c.name}?`)) delCat.mutate(c.id); }}
              data-testid={`category-del-${c.id}`}
              className="p-1 text-zinc-600 hover:text-[#FF3B30]"
              title="Delete category"
            ><X className="w-2.5 h-2.5"/></button>
          </div>
        ))}
        <input
          value={newCatName}
          onChange={(e) => setNewCatName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && newCatName.trim()) createCat.mutate({name: newCatName.trim()}); }}
          data-testid="category-new-input"
          placeholder="+ new category"
          className="bg-[#050505] border border-white/10 focus:border-white/30 outline-none px-2 py-1 text-[11px] font-mono w-32"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/10">
          {q.isLoading && <div className="p-6 text-zinc-500 text-sm">Loading…</div>}
          {filtered.length === 0 && (
            <div className="p-10 text-center">
              <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// {tab === "drift" ? "drift pool is empty" : "no subreddits saved"}</div>
              <div className="text-zinc-400 text-sm">
                {tab === "drift"
                  ? "Add subreddits and mark them as drift pool. Or bulk-import a CSV — the current tab will import as drift by default."
                  : "Add subreddits your team is targeting. They become selectable when orchestrating new campaigns."}
              </div>
            </div>
          )}
          {filtered.map((s, i) => (
            <div key={s.id} data-testid={`sub-row-${s.id}`} className={`px-4 sm:px-5 py-4 flex flex-col gap-2 hover:bg-[#0e0e10] ${i>0 ? "border-t border-white/5" : ""}`}>
              <div className="flex items-start gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <a href={`https://www.reddit.com/${s.name}/`} target="_blank" rel="noreferrer"
                       className="font-mono text-[14px] text-[#FF3B30] hover:underline flex items-center gap-1">
                      {s.name} <ExternalLink className="w-3 h-3"/>
                    </a>
                    {!s.active && <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-white/10 px-1.5 py-0.5">paused</span>}
                    {s.is_drift_pool && <span data-testid={`drift-pool-badge-${s.id}`} className="text-[10px] font-mono uppercase tracking-widest text-[#007AFF] border border-[#007AFF]/40 px-1.5 py-0.5 flex items-center gap-1"><Waves className="w-2.5 h-2.5"/> drift</span>}
                    {s.rules && <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5" data-testid={`sub-has-rules-${s.id}`}>rules loaded</span>}
                  </div>
                  <div className="text-[13px] text-zinc-400">{s.description || <em className="text-zinc-600">no description</em>}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-auto">
                  <button
                    onClick={() => toggleDrift.mutate({id: s.id, is_drift_pool: !s.is_drift_pool})}
                    title={s.is_drift_pool ? "Remove from drift pool" : "Add to drift pool"}
                    data-testid={`toggle-drift-${s.id}`}
                    className={`p-1.5 border ${s.is_drift_pool ? "border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF]/10" : "border-white/10 hover:border-[#007AFF]/50 text-zinc-400 hover:text-[#007AFF]"}`}
                  >
                    <Waves className="w-3.5 h-3.5"/>
                  </button>
                  <button
                    onClick={() => setEditing({ id: s.id, name: s.name, description: s.description || "", rules: s.rules || "" })}
                    title="Edit rules"
                    data-testid={`edit-sub-${s.id}`}
                    className="p-1.5 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white text-[10px] font-mono uppercase tracking-widest px-2"
                  >
                    edit
                  </button>
                  <button
                    onClick={() => toggle.mutate({id: s.id, active: !s.active})}
                    title={s.active ? "Pause" : "Reactivate"}
                    data-testid={`toggle-sub-${s.id}`}
                    className="p-1.5 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white"
                  >
                    <Power className="w-3.5 h-3.5"/>
                  </button>
                  <button
                    onClick={() => { if (window.confirm(`Remove ${s.name} from list?`)) del.mutate(s.id); }}
                    title="Remove"
                    data-testid={`delete-sub-${s.id}`}
                    className="p-1.5 border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30]"
                  >
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </div>
              {editing?.id === s.id && (
                <div className="mt-2 border border-[#FF3B30]/40 bg-[#050505] p-3" data-testid={`edit-panel-${s.id}`}>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Rules (injected into AI prompts + shown on drift tasks)</label>
                  <textarea
                    rows={6}
                    value={editing.rules}
                    onChange={(e)=>setEditing({...editing, rules: e.target.value})}
                    placeholder="Paste the subreddit's rules here. E.g. 'No self-promotion. Comments must add value. No affiliate links…'"
                    data-testid={`edit-rules-input-${s.id}`}
                    className="w-full bg-[#0a0a0c] border border-white/10 px-3 py-2 text-[12px] font-mono leading-relaxed mb-2"
                  />
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Description</label>
                  <input
                    value={editing.description}
                    onChange={(e)=>setEditing({...editing, description: e.target.value})}
                    data-testid={`edit-desc-input-${s.id}`}
                    className="w-full bg-[#0a0a0c] border border-white/10 px-3 py-2 mb-3 text-[13px]"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setEditing(null)}
                      className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 border border-white/10 px-3 py-1.5 hover:text-white"
                      data-testid={`cancel-edit-${s.id}`}
                    >cancel</button>
                    <button
                      onClick={() => saveEdit.mutate({ name: editing.name, description: editing.description, rules: editing.rules })}
                      disabled={saveEdit.isPending}
                      className="text-[11px] font-mono uppercase tracking-widest bg-[#FF3B30] hover:bg-[#FF5A50] text-white px-3 py-1.5 disabled:opacity-50"
                      data-testid={`save-edit-${s.id}`}
                    >{saveEdit.isPending ? "saving…" : "save"}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border border-white/10 p-5 h-fit">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Add subreddit</div>
          <h3 className="font-display font-black text-xl tracking-tight mb-4">Grow your target list.</h3>
          <input
            placeholder="r/gardening"
            value={form.name}
            onChange={(e)=>setForm({...form, name:e.target.value})}
            data-testid="new-sub-name"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-sm font-mono"
          />
          <textarea
            placeholder="Short description — audience, tone (optional but helps the AI)"
            rows={3}
            value={form.description}
            onChange={(e)=>setForm({...form, description:e.target.value})}
            data-testid="new-sub-desc"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-[13px] leading-relaxed"
          />
          <textarea
            placeholder="Subreddit rules (paste from the sub's sidebar) — will be injected into every AI orchestration for compliance"
            rows={4}
            value={form.rules}
            onChange={(e)=>setForm({...form, rules:e.target.value})}
            data-testid="new-sub-rules"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-[12px] font-mono leading-relaxed"
          />
          <label className="flex items-center gap-2 mb-4 text-[12px] text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_drift_pool || tab === "drift"}
              disabled={tab === "drift"}
              onChange={(e)=>setForm({...form, is_drift_pool: e.target.checked})}
              data-testid="new-sub-drift-checkbox"
              className="accent-[#007AFF]"
            />
            <Waves className="w-3.5 h-3.5 text-[#007AFF]"/>
            Add to drift pool
            {tab === "drift" && <span className="text-[10px] font-mono text-[#007AFF]">(auto — drift tab)</span>}
          </label>

          <div className="mb-3">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Category</label>
            <select
              value={form.category_id}
              onChange={(e)=>setForm({...form, category_id: e.target.value})}
              data-testid="new-sub-category"
              className="w-full bg-[#050505] border border-white/10 px-3 py-2 text-[13px]"
            >
              <option value="">— none —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Belongs to brand(s) <span className="text-zinc-600 normal-case tracking-normal">— leave empty for any brand</span></label>
            <BrandPicker selected={form.product_ids} onChange={(v) => setForm({...form, product_ids: v})} testIdPrefix="new-sub-brand"/>
          </div>

          <button
            onClick={submit}
            disabled={create.isPending}
            data-testid="create-sub-btn"
            className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {create.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
            ADD TO LIST
          </button>
          <div className="mt-4 pt-4 border-t border-white/5 text-[11px] font-mono text-zinc-500 leading-relaxed">
            Prefix with <code className="text-white">r/</code> is optional — we add it automatically. Use <b>Bulk XLSX</b> above to import hundreds at once (columns: <code className="text-white">name, description, rules, is_drift_pool</code>).
          </div>
        </div>
      </div>
    </Layout>
  );
}
